import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { formatInTz } from "../lib/dates";
import { Users2 } from "lucide-react";

export default function ReassignTasks() {
  const { t } = useTranslation();
  const { tenant } = useAuthStore();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [assignee, setAssignee] = useState("");

  const load = async () => {
    const [tRes, uRes] = await Promise.all([
      api.get("/users/unassigned-tasks"),
      api.get("/users"),
    ]);
    setTasks(tRes.data);
    setUsers(uRes.data.filter((u) => u.active));
  };
  useEffect(() => {
    load();
  }, []);

  const toggle = (id) => {
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );
  };
  const selectAll = () => {
    setSelected(selected.length === tasks.length ? [] : tasks.map((t) => t.id));
  };

  const submit = async () => {
    if (!selected.length) return;
    if (!assignee) {
      toast.error(t("reassign.pick_user"));
      return;
    }
    try {
      const { data } = await api.post("/users/bulk-reassign", {
        task_ids: selected,
        assignee_id: assignee,
      });
      toast.success(t("reassign.done", { count: data.updated }));
      setSelected([]);
      setAssignee("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("reassign.title")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">{t("reassign.subtitle")}</p>
      </div>

      {tasks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-16 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-zinc-100">
            <Users2 className="h-5 w-5 text-zinc-500" />
          </div>
          <p className="text-sm text-zinc-500">{t("reassign.empty")}</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={selected.length === tasks.length}
                onCheckedChange={selectAll}
                data-testid={TEST_IDS.reassignSelectAll}
              />
              <span className="text-sm text-zinc-700">
                {selected.length} / {tasks.length} {t("reassign.selected")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger
                  className="w-52"
                  data-testid={TEST_IDS.reassignPickUser}
                >
                  <SelectValue placeholder={t("reassign.pick_user")} />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                disabled={!selected.length || !assignee}
                onClick={submit}
                data-testid={TEST_IDS.reassignSubmit}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {t("reassign.assign")}
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <ul className="divide-y divide-zinc-100">
              {tasks.map((task) => (
                <li
                  key={task.id}
                  data-testid={TEST_IDS.reassignRow(task.id)}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors"
                >
                  <Checkbox
                    checked={selected.includes(task.id)}
                    onCheckedChange={() => toggle(task.id)}
                    data-testid={TEST_IDS.reassignRowCheckbox(task.id)}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-zinc-900">
                      {task.title}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {task.priority} ·{" "}
                      {task.due_at
                        ? formatInTz(task.due_at, tenant?.timezone, "DD MMM HH:mm")
                        : "no due date"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
